import { DotLottie } from '@lottiefiles/dotlottie-web';
import { Component, Element, h, Host, Prop, State } from '@stencil/core';
import { handleRedirectWithDisclaimerLogic } from '@utils/utilities';
import { Disclaimer, PartnerDisclaimerData } from '../types';
import { FooterLink } from './types';

@Component({
  tag: 'common-footer',
  styleUrl: 'common-footer.scss',
  shadow: true
})
export class CommonFooter {
  @Prop() assetPath: string;
  @Prop() showLottie: boolean = true;
  @Prop() footerContents: any;
  @Prop() disclaimerData: PartnerDisclaimerData;
  @Prop() isVisible: boolean = true;
  @Prop() isMain: boolean = false;
  @Element() el: HTMLElement;
  @State() disclaimerContent: Disclaimer
  @State() isMobile: boolean = window.innerWidth <= 640;
  @State() showRedirectPopup: boolean = false;
  @State() redirectPopupContentType: string;
  @State() redirectTargetUrl: string = '';

  currentYear: number = new Date().getFullYear();

  componentWillLoad() {
    if (typeof this.footerContents === 'string') {
      const decoded = atob(this.footerContents);
      this.footerContents = JSON.parse(decoded);
    }
    // const parsedDisclaimerData = typeof this.disclaimerData === 'string' ? JSON.parse(atob(this.disclaimerData)) : this.disclaimerData;

    // if (parsedDisclaimerData) {
    //   disclaimerStore.disclaimerData = parsedDisclaimerData;
    // }
    // pathStore.assetPath = this.assetPath;

  }

  handleRedirectClose = () => {
    this.showRedirectPopup = false;
  };

  handleRedirectProceed = () => {
    if (this.redirectTargetUrl) {
      window.location.href = this.redirectTargetUrl;
    }
  };

  onLinkClick = (e: Event, item: FooterLink) => {
    e.preventDefault();

    const { redirectUrl, disclaimer_popup, openRedirectUrlInNewTab } = item;

    const result = handleRedirectWithDisclaimerLogic({
      redirectUrl: redirectUrl,
      disclaimerSlug: disclaimer_popup?.slug,
      openInNewTab: openRedirectUrlInNewTab
    });

    if (result.showPopup) {
      this.redirectPopupContentType = result.contentType;
      this.redirectTargetUrl = result.redirectUrl;
      this.disclaimerContent = result.disclaimerContent;
      this.showRedirectPopup = true;
    }
  };

  private loadDotLottie(canvas: HTMLCanvasElement, paths: string[]) {
    const tryNext = () => {
      if (paths.length === 0) {
        console.warn('No .lottie file found for the given context.');
        return;
      }

      const next = paths.shift();
      const src = `${this.assetPath}images/footer_animation_files/${next}.lottie`;
      new DotLottie({
        canvas,
        autoplay: true,
        loop: true,
        src
      });
    };

    tryNext();
  }

  private observeCanvasAndLoadLottie(canvas: HTMLCanvasElement) {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          if (!canvas) return;
          const deviceType = this.isMobile ? 'mlite' : 'desktop';
          const rawTitle = this.footerContents?.card_program?.title?.toLowerCase?.().replace(/\s+/g, '-');
          const rawType = this.footerContents?.card_program?.type?.toLowerCase?.().replace(/\s+/g, '-');

          const tryPaths: string[] = [];

          if (rawTitle) tryPaths.push(`${rawTitle}_footer_${deviceType}`);
          if (rawType) tryPaths.push(`${rawType}_footer_${deviceType}`);
          tryPaths.push(`default_footer_${deviceType}`);
          resizeObserver.disconnect();
          this.loadDotLottie(canvas, tryPaths);
        }
      }
    });
    resizeObserver.observe(canvas);
  }


  componentDidRender() {
    if (this.showLottie) {
      const canvas = this.el.shadowRoot?.querySelector('#dotlottie-canvas') as HTMLCanvasElement;
      if (canvas) {
        this.observeCanvasAndLoadLottie(canvas);
      }
    }
  }


  render() {
    if (!this.isVisible) {
      return null;
    }
    const shouldRenderFooter = !!(this.footerContents?.footer_links?.length || this.footerContents?.disclaimerText || this.footerContents?.mobile1 || this.footerContents?.email1);

    return (
      <Host>
        {shouldRenderFooter && (
          <div class="bg-gray-100 sm:border-t-2 sm:border-blue-900/30">
            <div class="max-w-7xl mx-auto px-5 sm:py-10">
              <div class="flex flex-col sm:flex-row sm:justify-between flex-wrap">
                <div class="order-1 w-full md:w-1/2">
                  {this.footerContents.footer_links?.length > 0 && (
                    <div class="flex flex-col sm:flex-row flex-wrap sm:gap-5 text-sm text-blue-700 font-normal py-3">
                      {this.footerContents.footer_links.map((item, index) => (
                        <a
                          href={item.redirectUrl}
                          key={index}
                          class="text-blue-700 font-medium hover:underline border-b border-dashed border-blue-900/10 py-3 sm:py-0 sm:border-0 last:border-0 flex justify-between"
                          onClick={e => this.onLinkClick(e, item)}
                        >
                          {item.title}
                          <span class="block sm:hidden">
                            <img alt="smartbuy" src={`${this.assetPath}images/icons/right_arrow.svg`} class="w-2 h-4" />
                          </span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>

                <div class="order-2 mt-1 mb-1 sm:mt-0 sm:mb-0 w-full sm:w-auto">
                  <footer-contact-info
                    assetPath={this.assetPath}
                    footerSupportContactNo1={this.footerContents?.mobile1}
                    footerSupportContactNo2={this.footerContents?.mobile2}
                    footerSupportEmail1={this.footerContents?.email1}
                    footerSupportEmail2={this.footerContents?.email2}
                  />
                </div>

                <div class="order-3 w-full md:w-1/2 mb-4 sm:mb-0">
                  <p class="mt-4 md:mt-0 text-[11px] text-gray-700 leading-4 font-normal">{this.footerContents?.disclaimerText}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div class="footer-gradient w-full">
          <div class="relative text-white w-full">
            <div class={`${!shouldRenderFooter ? 'bg-white' : 'bg-gray-100'} relative w-full h-9`}>
              <div
                style={{
                  backgroundImage: `url(${this.assetPath}${this.isMobile ? 'images/wave_bg.svg' : 'images/wave_bg_desktop.svg'})`
                }}
                class="bg-repeat-x bg-top h-9 w-full md:absolute md:-bottom-1"
              />
            </div>

            <div class="py-5 px-5">
              <p class="font-[PhosphateSolid] font-bold text-4xl md:text-5xl leading-none tracking-wider text-center uppercase text-blue-200/30">
                smartbuy. where deals get better.
              </p>
            </div>

            {this.showLottie && <canvas id="dotlottie-canvas" class={`hidden lg:block w-full md:h-[244px] h-[180px]`} />}
          </div>
        </div>

        <div class="copyrights_bg text-white border-b-2 border-blue-900/30 px-5 py-5 mb-[60px] md:mb-0">
          <div class="mx-auto flex justify-between items-center text-xs md:text-sm flex-wrap gap-3 text-center md:text-left font-normal">
            <span>&copy; {this.currentYear} SmartBuy</span>
            <span class="flex items-center gap-2">
              Powered By <img alt="smartbuy" src={`${this.assetPath}images/poweredBy_r360logo.svg`} class="w-12 h-4" />
            </span>
          </div>
        </div>

        {this.showRedirectPopup && (
          <redirect-popup
            open={this.showRedirectPopup}
            contentType={this.redirectPopupContentType}
            onConfirm={this.handleRedirectProceed}
            onClose={this.handleRedirectClose}
            redirectTargetUrl={this.redirectTargetUrl}
            disclaimerContent={this.disclaimerContent}
          />
        )}
      </Host>
    );
  }
}
